import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Plus, 
  Search, 
  Filter,
  MoreHorizontal,
  Pencil,
  Trash2,
  FileText,
  Download,
  Loader2,
  Upload,
  Eye,
  FileDown
} from "lucide-react";
import { Layout } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { RelatorioFilterModal } from "../components/RelatorioFilterModal";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { apiRequest, formatDate, API_URL, getToken } from "../lib/utils";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "../components/ToastProvider";
import { AdminPasswordDialog } from "../components/AdminPasswordDialog";

const PERIODICIDADES = [
  { value: "nunca", label: "Nunca (não vence)" },
  { value: "diária", label: "Diária" },
  { value: "diaria", label: "Diária" },
  { value: "semanal", label: "Semanal" },
  { value: "quinzenal", label: "Quinzenal" },
  { value: "mensal", label: "Mensal" },
  { value: "bimestral", label: "Bimestral" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
  { value: "bianual", label: "Bianual" },
];

const STATUS_OPTIONS = [
  { value: "em_dia", label: "Em dia" },
  { value: "vencendo", label: "Vencendo" },
  { value: "vencido", label: "Vencido" },
];

export const DocumentosList = () => {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [periodicidadeFilter, setPeriodicidadeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDocName, setDeleteDocName] = useState("");
  const [adminPasswordOpen, setAdminPasswordOpen] = useState(false);
  const [relatorioModalOpen, setRelatorioModalOpen] = useState(false);

  useEffect(() => {
    fetchDocumentos();
  }, []);

  const fetchDocumentos = async () => {
    try {
      const data = await apiRequest("/documentos");
      setDocumentos(data);
    } catch (error) {
      console.error("Erro ao carregar documentos:", error);
      toast.error("Erro ao carregar documentos");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await apiRequest(`/documentos/${deleteId}`, { method: "DELETE" });
      toast.success("Documento excluido com sucesso!");
      setDeleteId(null);
      setDeleteDocName("");
      fetchDocumentos();
    } catch (error) {
      toast.error("Erro ao excluir documento");
    }
  };

  const confirmDelete = (doc) => {
    setDeleteId(doc.id);
    setDeleteDocName(doc.nome_documento || doc.tipo_documento);
    setAdminPasswordOpen(true);
  };

  const getPeriodicidadeLabel = (value) => {
    if (!value) return "-";
    const found = PERIODICIDADES.find(p => p.value === value.toLowerCase());
    if (found) return found.label;
    // Para periodicidades customizadas, capitalizar a primeira letra
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const filteredDocumentos = documentos.filter((doc) => {
    const matchesSearch = doc.nome_documento.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPeriodicidade = periodicidadeFilter === "all" || doc.periodicidade === periodicidadeFilter;
    const matchesStatus = statusFilter === "all" || doc.status === statusFilter;
    return matchesSearch && matchesPeriodicidade && matchesStatus;
  });

  return (
    <Layout>
      <div className="space-y-6 animate-slide-in" data-testid="documentos-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Documentos e Alvarás
            </h1>
            <p className="text-slate-500 mt-1">Aqui você vai cadastrar documentos como renovação de Alvará, CRF, Siproquim, Mapa e outros documentos que são renovados anualmente ou em outros períodos.</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setRelatorioModalOpen(true)}
              data-testid="download-pdf-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Relatório PDF
            </Button>
            <Button asChild className="bg-teal-600 hover:bg-teal-700" data-testid="new-documento-btn">
              <Link to="/documentos/novo">
                <Plus className="w-4 h-4 mr-2" />
                Novo Documento
              </Link>
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar documentos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={periodicidadeFilter} onValueChange={setPeriodicidadeFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="periodicidade-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Periodicidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {PERIODICIDADES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filteredDocumentos.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900">Nenhum documento encontrado</h3>
                <p className="text-slate-500 mt-1">
                  {searchTerm || periodicidadeFilter !== "all" || statusFilter !== "all"
                    ? "Tente ajustar os filtros"
                    : "Comece cadastrando seu primeiro documento"}
                </p>
                {!searchTerm && periodicidadeFilter === "all" && statusFilter === "all" && (
                  <Button asChild className="mt-4 bg-teal-600 hover:bg-teal-700">
                    <Link to="/documentos/novo">
                      <Plus className="w-4 h-4 mr-2" />
                      Cadastrar documento
                    </Link>
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Nome do Documento</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600 hidden md:table-cell">Periodicidade</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600 hidden lg:table-cell">Última Execucao</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Vencimento</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600 hidden md:table-cell">Responsável</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Status</th>
                      <th className="text-right p-4 text-sm font-semibold text-slate-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocumentos.map((doc) => (
                      <tr 
                        key={doc.id} 
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                        data-testid={`documento-row-${doc.id}`}
                      >
                        <td className="p-4">
                          <Link 
                            to={`/documentos/${doc.id}`}
                            className="font-medium text-slate-900 hover:text-teal-600 transition-colors"
                          >
                            {doc.nome_documento}
                          </Link>
                          <p className="text-sm text-slate-500 md:hidden">
                            {getPeriodicidadeLabel(doc.periodicidade)}
                          </p>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <span className="text-sm text-slate-600">
                            {getPeriodicidadeLabel(doc.periodicidade)}
                          </span>
                        </td>
                        <td className="p-4 hidden lg:table-cell">
                          <span className="text-sm text-slate-600">
                            {doc.data_ultima_execucao ? formatDate(doc.data_ultima_execucao) : "-"}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-slate-900">
                            {formatDate(doc.data_proximo_vencimento)}
                          </span>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <span className="text-sm text-slate-600">
                            {doc.responsavel || "-"}
                          </span>
                        </td>
                        <td className="p-4">
                          <StatusBadge status={doc.status} />
                        </td>
                        <td className="p-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`documento-actions-${doc.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/documentos/${doc.id}`} className="cursor-pointer">
                                  <Eye className="w-4 h-4 mr-2" />
                                  Ver detalhes
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={`/documentos/${doc.id}/editar`} className="cursor-pointer">
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Editar
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => confirmDelete(doc)}
                                className="text-red-600 cursor-pointer"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin Password Dialog para Excluir */}
        <AdminPasswordDialog
          open={adminPasswordOpen}
          onOpenChange={(open) => {
            setAdminPasswordOpen(open);
            if (!open) {
              setDeleteId(null);
              setDeleteDocName("");
            }
          }}
          onConfirm={handleDelete}
          title="Excluir Documento"
          description="Esta ação não pode ser desfeita."
          itemName={deleteDocName}
        />

        {/* Modal de Filtros para Relatório */}
        <RelatorioFilterModal
          open={relatorioModalOpen}
          onOpenChange={setRelatorioModalOpen}
          title="Relatório de Documentos e Alvarás"
          description="Selecione os filtros para gerar o relatório em PDF"
          endpoint="/pdf/documentos"
          filters={[
            {
              key: "periodicidade",
              label: "Periodicidade",
              type: "select",
              options: PERIODICIDADES
            },
            {
              key: "status",
              label: "Status",
              type: "select",
              options: STATUS_OPTIONS
            },
            {
              key: "responsavel",
              label: "Responsável",
              type: "text",
              placeholder: "Filtrar por responsável..."
            },
            {
              key: "data_inicio",
              label: "Vencimento a partir de",
              type: "date"
            },
            {
              key: "data_fim",
              label: "Vencimento até",
              type: "date"
            }
          ]}
        />
      </div>
    </Layout>
  );
};

export default DocumentosList;
